/**
 * shared/schema/qr-subsystem.ts — Task #1964.
 *
 * Universal QR Subsystem schema. Three tables:
 *
 *   - `qr_kind_policies`   — per-tenant override of the per-kind policy
 *                            defaults declared in `shared/qr-kinds.ts`.
 *   - `qr_token_issuances` — projection of every QR mint, indexed for
 *                            the SA QR Console (filterable by kind +
 *                            tenant + actor + window).
 *   - `jti_replay_cache`   — Postgres-backed JTI replay protection
 *                            shared across all QR kinds. Cross-pod by
 *                            design (replaces in-process Maps).
 *
 * NEW shared file — documented as a deliberate user-pref deviation in
 * `.local/.commit_message` (precedent: Task #1960's
 * `shared/schema/public-surface-hardening.ts`). Justified because the
 * SA QR Console (client) projects rows from these tables directly via
 * the typed `$inferSelect` shapes.
 */
import { sql } from "drizzle-orm";
import { boolean, index, integer, jsonb, pgTable, text, timestamp, uniqueIndex, varchar } from "drizzle-orm/pg-core";

/** Per-tenant per-kind policy override. */
export const qrKindPolicies = pgTable(
  "qr_kind_policies",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    tenantId: varchar("tenant_id").notNull(),
    qrKind: varchar("qr_kind", { length: 64 }).notNull(),
    maxTtlSeconds: integer("max_ttl_seconds"),
    replayWindowSeconds: integer("replay_window_seconds"),
    allowCrossTenant: boolean("allow_cross_tenant"),
    enabled: boolean("enabled").notNull().default(true),
    notes: text("notes"),
    updatedBy: varchar("updated_by"),
    createdAt: timestamp("created_at").notNull().defaultNow(),
    updatedAt: timestamp("updated_at").notNull().defaultNow(),
  },
  (t) => ({
    uqTenantKind: uniqueIndex("qr_kind_policies_tenant_kind_uq").on(t.tenantId, t.qrKind),
    idxKind: index("qr_kind_policies_kind_idx").on(t.qrKind),
  }),
);

export type QrKindPolicy = typeof qrKindPolicies.$inferSelect;
export type InsertQrKindPolicy = typeof qrKindPolicies.$inferInsert;

/** Projection of every QR mint — fuels the SA QR Console. */
export const qrTokenIssuances = pgTable(
  "qr_token_issuances",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    tenantId: varchar("tenant_id").notNull(),
    qrKind: varchar("qr_kind", { length: 64 }).notNull(),
    jti: varchar("jti", { length: 128 }).notNull(),
    keyId: varchar("key_id", { length: 128 }).notNull(),
    /** Subject id — workerId / credentialId / equipmentId / spaceId / partnerLinkId. */
    subjectId: varchar("subject_id"),
    actorId: varchar("actor_id"),
    actorRole: varchar("actor_role", { length: 64 }),
    issuedAt: timestamp("issued_at").notNull().defaultNow(),
    expiresAt: timestamp("expires_at"),
    revokedAt: timestamp("revoked_at"),
    revokedReason: text("revoked_reason"),
    /** Free-form context: ip, ua, source surface, kind-specific extras. */
    context: jsonb("context"),
  },
  (t) => ({
    uqJti: uniqueIndex("qr_token_issuances_jti_uq").on(t.jti),
    idxTenantIssuedAt: index("qr_token_issuances_tenant_issued_idx").on(t.tenantId, t.issuedAt),
    idxKindIssuedAt: index("qr_token_issuances_kind_issued_idx").on(t.qrKind, t.issuedAt),
    idxSubject: index("qr_token_issuances_subject_idx").on(t.subjectId),
  }),
);

export type QrTokenIssuance = typeof qrTokenIssuances.$inferSelect;
export type InsertQrTokenIssuance = typeof qrTokenIssuances.$inferInsert;

/**
 * JTI replay cache — every successful verification records a row here
 * keyed by jti. The TTL is `expires_at`; a periodic sweep (or a simple
 * "where expires_at > now()" check on lookup) discards stale rows. The
 * verifier MUST treat the presence of an existing row as a replay.
 */
export const jtiReplayCache = pgTable(
  "jti_replay_cache",
  {
    jti: varchar("jti", { length: 128 }).primaryKey(),
    tenantId: varchar("tenant_id").notNull(),
    qrKind: varchar("qr_kind", { length: 64 }).notNull(),
    seenAt: timestamp("seen_at").notNull().defaultNow(),
    expiresAt: timestamp("expires_at").notNull(),
    verifierId: varchar("verifier_id"),
  },
  (t) => ({
    idxExpires: index("jti_replay_cache_expires_idx").on(t.expiresAt),
    idxTenantKind: index("jti_replay_cache_tenant_kind_idx").on(t.tenantId, t.qrKind),
  }),
);

export type JtiReplayCacheRow = typeof jtiReplayCache.$inferSelect;
export type InsertJtiReplayCacheRow = typeof jtiReplayCache.$inferInsert;
